import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { IFileFormFieldCropOptions } from 'lib/ui/form/fields/file-form-field.component.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { Observable, map } from 'rxjs';
import { environment } from 'src/app/environments/environment.js';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { IBrandingRouteData } from 'src/app/routes/profile/branding-routes.js';

@Component({
	selector: 'mod-main-branding-form',
	templateUrl: './main-branding-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class MainBrandingFormComponent extends TypeCoreComponent<IBrandingRouteData> implements OnInit {
	public readonly brandNameMaxLength: number = 20;
	public readonly subdomainMaxLength: number = 20;

	public readonly imageCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 200
	};

	public readonly subdomainSubtitle = `Subdomain is used to create a branded custom URL for your brand.`;
	public readonly subdomainSuffix = computed<string>(() => {
		const url = new URL(environment.productionUrl);

		return `.${url.hostname}`;
	});

	public readonly form = {
		brandBio: signal<string | undefined>(undefined),
		brandName: signal<string | undefined>(undefined),
		brandTel: signal<string | undefined>(undefined),
		brandingSubdomain: signal<string | undefined>(undefined),
		brandLogo: signal<CosmosDb.ICosmosFileReference[]>([])
	};

	public readonly uploadFunction: (file: File) => Observable<CosmosDb.ICosmosFileReference[]> = (file) => {
		return this.dependencies.httpService
			.uploadCustomFile<CosmosDb.ICosmosFileReference>((builder) =>
				builder.withCustomApiRoute('user', 'uploadBrandLogo').withFile(file).build()
			)
			.pipe(
				map((response) => {
					return [response.data];
				})
			);
	};

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const form = this.form;

		const brandName = form.brandName();

		if (!brandName) {
			return undefined;
		}

		return this.dependencies.httpService
			.post<UserApi.IUserResponseWithToken, UserDb.IUserBranding>((builder) =>
				builder
					.withCustomApiRoute('user', 'editBranding')
					.withData({
						brandName: brandName,
						brandLogo: form.brandLogo(),
						brandBio: form.brandBio(),
						brandTel: form.brandTel(),
						brandingSubdomain: form.brandingSubdomain()
					})
					.build()
			)
			.pipe(
				map((response) => {
					super.notifySaved('Branding');

					// update current branding
					super.subscribeToObservable(this.dependencies.coreAppService.initAppContext());
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.form.brandLogo.set(sharedData?.data.user.branding?.brandLogo ?? []);
			this.form.brandBio.set(sharedData?.data.user.branding?.brandBio);
			this.form.brandTel.set(sharedData?.data.user.branding?.brandTel);
			this.form.brandingSubdomain.set(sharedData?.data.user.branding?.brandingSubdomain);
			this.form.brandName.set(sharedData?.data.user.branding?.brandName ?? environment.appName);
		};
	}

	handleBrandLogoChange(files: CosmosDb.ICosmosFileReference[] | undefined): void {
		if (files && this.form) {
			this.form.brandLogo.set(files);

			const saveHandler = this.saveHandler();
			if (saveHandler) {
				super.subscribeToObservable(saveHandler);
			}
		}
	}
}
